import { Box } from '@mui/material'

import React, {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

/** Keep carousel controls from triggering a parent card/link navigation. */
function stopParentNavigation(event) {
  if (!event) return
  event.preventDefault()
  event.stopPropagation()
}

/**
 * Drop-in for `react-slick` covering the props this app uses.
 * Keeps slick-* class names so CssBaseline slick rules still paint.
 */
const Slider = forwardRef(function Slider(
  {
    children,
    className = '',
    style,
    dots = false,
    arrows = true,
    speed = 500,
    slidesToShow = 1,
    slidesToScroll = 1,
    infinite = true,
    adaptiveHeight = false,
    fade = false,
    nextArrow,
    prevArrow,
    beforeChange,
    afterChange,
    ..._rest
  },
  ref,
) {
  const slides = useMemo(
    () => Children.toArray(children).filter(Boolean),
    [children],
  )
  const count = slides.length
  const [index, setIndex] = useState(0)

  const goTo = useCallback(
    (next, fromIndex = index) => {
      if (count <= 0) return
      let target = next
      if (infinite) {
        target = ((next % count) + count) % count
      } else {
        target = Math.max(0, Math.min(count - 1, next))
      }
      if (target === fromIndex) return
      beforeChange?.(fromIndex, target)
      setIndex(target)
      window.setTimeout(() => afterChange?.(target), speed)
    },
    [afterChange, beforeChange, count, index, infinite, speed],
  )

  const next = useCallback(() => {
    goTo(index + slidesToScroll)
  }, [goTo, index, slidesToScroll])

  const prev = useCallback(() => {
    goTo(index - slidesToScroll)
  }, [goTo, index, slidesToScroll])

  useImperativeHandle(
    ref,
    () => ({
      slickGoTo: (i) => goTo(i),
      slickNext: next,
      slickPrev: prev,
    }),
    [goTo, next, prev],
  )

  useEffect(() => {
    if (index >= count && count > 0) setIndex(0)
  }, [count, index])

  const renderArrow = (arrow, defaultClass, onClick) => {
    if (arrows === false) return null
    if (isValidElement(arrow)) {
      return cloneElement(arrow, {
        className: [defaultClass, arrow.props.className]
          .filter(Boolean)
          .join(' '),
        onClick: (event) => {
          stopParentNavigation(event)
          arrow.props.onClick?.(event)
          onClick(event)
        },
      })
    }
    return (
      <button
        type="button"
        className={defaultClass}
        onClick={(event) => {
          stopParentNavigation(event)
          onClick(event)
        }}
        aria-label={defaultClass.includes('next') ? 'Next' : 'Previous'}
      />
    )
  }

  if (count === 0) {
    return (
      <div className={['slick-slider', className].filter(Boolean).join(' ')} style={style} />
    )
  }

  // slidesToShow > 1 is unused in this app; keep a simple single-slide track.
  void slidesToShow

  return (
    <div
      className={['slick-slider', 'slick-initialized', className]
        .filter(Boolean)
        .join(' ')}
      style={{ overflow: 'hidden', position: 'relative', ...style }}
      onClickCapture={(event) => {
        if (
          event.target.closest?.(
            '.slick-arrow, .slick-prev, .slick-next, .slick-dots',
          )
        ) {
          event.preventDefault()
        }
      }}
    >
      {renderArrow(prevArrow, 'slick-arrow slick-prev', prev)}
      <Box sx={{
        position: "relative",
        overflow: "hidden",
        display: "block",
        margin: "0",
        padding: "0",
      }}>
        <Box style={ fade ? { position: 'relative', width: '100%', } : { display: 'flex', width: `${count * 100}%`, transform: `translate3d(-${(100 / count) * index}%, 0, 0)`, transition: `transform ${speed}ms ease`, alignItems: adaptiveHeight ? 'flex-start' : undefined, } } sx={{
          position: "relative",
          left: "0",
          top: "0",
          display: "block",
          marginLeft: "auto",
          marginRight: "auto",
        }}>
          {slides.map((slide, i) => (
            <div
              key={slide.key ?? i}
              className={[
                'slick-slide',
                i === index ? 'slick-active slick-current' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={
                fade
                  ? {
                      width: '100%',
                      opacity: i === index ? 1 : 0,
                      position: i === index ? 'relative' : 'absolute',
                      top: 0,
                      left: 0,
                      transition: `opacity ${speed}ms ease`,
                      pointerEvents: i === index ? 'auto' : 'none',
                    }
                  : {
                      width: `${100 / count}%`,
                      flexShrink: 0,
                    }
              }
              data-index={i}
            >
              <div>{slide}</div>
            </div>
          ))}
        </Box>
      </Box>
      {renderArrow(nextArrow, 'slick-arrow slick-next', next)}
      {dots && count > 1 ? (
        <Box
          component="ul"
          className="slick-dots"
          sx={[{
            listStyle: "none",
            display: "block",
            textAlign: "center",
            padding: "0",
            margin: "0",
            width: "100%",
            height: "8px",
            position: "absolute",
            bottom: "15px",
            left: "0",
            right: "0",
            zIndex: 2,
            "& li": {
              position: "relative",
              display: "inline-block",
              padding: 0,
              margin: "0 0.3333333333rem",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
            },
            "& li button": {
              border: 0,
              background: "#d1d2db",
              display: "block",
              height: "8px",
              width: "8px",
              padding: 0,
              cursor: "pointer",
              position: "relative",
              borderRadius: "0.65rem",
              color: "transparent",
              fontSize: 0,
              lineHeight: 0,
              overflow: "hidden",
              transition: "all 0.2s ease-in-out",
              outline: "none",
            },
            "& li.slick-active button": { width: "26px", background: "#368cff" },
          }, {
            listStyle: "none",
            display: "block",
            textAlign: "center",
            padding: "0",
            margin: "0",
            width: "100%",
            height: "8px",
            position: "absolute",
            bottom: "15px",
            left: "0",
            right: "0",
            zIndex: 2,
            "& li": {
              position: "relative",
              display: "inline-block",
              padding: 0,
              margin: "0 0.3333333333rem",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
            },
            "& li button": {
              border: 0,
              background: "#d1d2db",
              display: "block",
              height: "8px",
              width: "8px",
              padding: 0,
              cursor: "pointer",
              position: "relative",
              borderRadius: "0.65rem",
              color: "transparent",
              fontSize: 0,
              lineHeight: 0,
              overflow: "hidden",
              transition: "all 0.2s ease-in-out",
              outline: "none",
            },
            "& li.slick-active button": { width: "26px", background: "#368cff" },
          }]}
        >
          {slides.map((slide, i) => (
            <li
              key={slide.key ?? `dot-${i}`}
              className={i === index ? 'slick-active' : undefined}
            >
              <button
                type="button"
                onClick={(event) => {
                  stopParentNavigation(event)
                  goTo(i)
                }}
                aria-label={`Slide ${i + 1}`}
              >
                {i + 1}
              </button>
            </li>
          ))}
        </Box>
      ) : null}
    </div>
  )
})

export default Slider
